import { TAppDispatch, TGetState } from 'store/types'
import { getUser_API, getUserSubscriptions_API, getUserStats_API } from 'api'
import slice from 'store/features/user/slice'
import { userSelectors } from 'store/features/user/selectors'
import {
  getActiveHighestSubscriptionData,
  getUserCurrentHighestSubscription,
  collectUniqueUnlimitedAiImageModelIds,
  collectUniqueUnlimitedAiVideoModels
} from 'utils/user'
// import { enableSentryReplay } from 'sentry/replay'
import * as Sentry from '@sentry/react'
import { ErrorKeys } from 'consts/errorKeys'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { handleApiError } from 'store/utils'

const actions = slice.actions

const getUserData = () => async (dispatch: TAppDispatch) => {
  try {
    dispatch(actions.setIsLoading(true))
    const response = await getUser_API()
    const user = response.data

    if (user?.id) {
      dispatch(actions.setUser(user))
      // Temporary enable Sentry replay for all users
      // if (user.preferences?.isSentryReplayEnabled) {
      // enableSentryReplay()
      Sentry.setTag('userId', user.id)
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: `${user.firstName} ${user.lastName}`
      })
      // }
    }
  } finally {
    dispatch(actions.setIsLoading(false))
  }
}

const initializeAppAuth =
  () => async (dispatch: TAppDispatch, getState: TGetState) => {
    await dispatch(getUserData())

    const state = getState()
    const isLoggedIn = userSelectors.isUserLoggedIn(state)

    if (!isLoggedIn) {
      window.openRFAuthModal(
        {
          initialView: 'signin',
          preventCloseOutsideClick: true
        },
        () => {
          window.location.reload()
        }
      )
    }
  }

const getActiveHighestSubscription = () => async (dispatch: TAppDispatch) => {
  const params = {
    status: 'active',
    orderBy: 'tariff_plan_order',
    order: 'DESC',
    limit: 10
  }

  try {
    dispatch(actions.setActiveHighestSubscriptionIsLoading(true))
    const response = await getUserSubscriptions_API(params)
    const subscriptions = response.data

    const currentSubscription = getUserCurrentHighestSubscription(subscriptions)
    if (currentSubscription) {
      dispatch(actions.setUserCurrentTariffId(currentSubscription.tariffId))
    }

    dispatch(
      actions.setUnlimitedAiImageModelIds(
        collectUniqueUnlimitedAiImageModelIds(subscriptions)
      )
    )
    dispatch(
      actions.setUnlimitedAiVideoModels(
        collectUniqueUnlimitedAiVideoModels(subscriptions)
      )
    )

    const highestSubscription = getActiveHighestSubscriptionData(subscriptions)
    if (highestSubscription) {
      dispatch(actions.setActiveHighestSubscription(highestSubscription))
    }
  } finally {
    dispatch(actions.setActiveHighestSubscriptionIsLoading(false))
  }
}

const getUserStats = () => async (dispatch: TAppDispatch) => {
  try {
    const response = await getUserStats_API()
    dispatch(actions.setUserStats(response.data))
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.captureException(new ReduxThunkError(error))
    }
    dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
  }
}

export const userThunks = {
  ...actions,
  getUserData,
  initializeAppAuth,
  getActiveHighestSubscription,
  getUserStats
}
